import React, { ReactElement } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppButton from "@/features/line-sheet-sets/app-button";
import { buyerRequestsMutationApi } from "@/shared/api/buyer-requests/mutation";
import useIdentity from "@/features/ui/hooks/use-identity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import ToggleTab from "@/shared/components/toggle-tab";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function BrandRequestModal({ isOpen, onClose }: Props) {
  const { tTitle, tSentence } = useI18nHelper();
  const identity = useIdentity();
  const [brandId, setBrandId] = React.useState<string>("");
  const { info } = useAppToasts({ id: "GLOBAL" });
  const [typeIdx, setTypeIdx] = React.useState<number>(0);

  const handleClose = () => {
    setBrandId("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent borderRadius={"8px"} overflow={"hidden"}>
        <ModalHeader background={"#F7F9FA"} padding={"12px 16px"}>
          <Text textStyle={"body-md-bold"} color={"#444444"}>
            {tTitle("request")}
          </Text>
        </ModalHeader>
        <ModalBody display="flex" flexDirection={"column"} gap={"24px"}>
          <Text textStyle={"body-md-regular"} color={"#6F6F6C"}>
            {tSentence("request_brand_modal")}
          </Text>
          <ToggleTab
            leftText={"브랜드"}
            rightText={"SKU"}
            onChange={(index) => {
              setTypeIdx(index);
              setBrandId("");
            }}
            index={typeIdx}
            width={"100%"}
          />
          <FormControl>
            <FormLabel color={"#6F6F6C"}>
              <Text textStyle={"body-sm-regular"}>
                {typeIdx === 0 ? "브랜드" : "SKU"}
              </Text>
            </FormLabel>
            <Input
              height={"28px"}
              placeholder={"요청 사항을 입력해 주세요."}
              fontSize={"13px"}
              fontWeight={"400"}
              padding={"4px 8px"}
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter display={"flex"} justifyContent={"end"} gap={"12px"}>
          <AppButton variant={"normal"} onClick={handleClose}>
            <Text textStyle={"body-sm-bold"} color={"#444444"}>
              {tTitle("cancel")}
            </Text>
          </AppButton>
          <AppButton
            variant={"primary"}
            isDisabled={brandId === ""}
            onClick={async () => {
              if (typeIdx === 0) {
                if (!identity?.company) {
                  handleClose();
                  return;
                }
                try {
                  // /buyers/{buyer_id}/… 의 buyer_id 는 바이어 회사 id 다. identity.id 는
                  // 로그인한 유저 id 라 넣으면 엉뚱한(또는 없는) 바이어로 요청이 쌓인다.
                  const res = await buyerRequestsMutationApi.addBrand(
                    identity.company.id,
                    {
                      brand_name: brandId,
                    }
                  );

                  if (res.status === 200) {
                    info("요청 사항이 제출되었습니다.");
                  }
                  handleClose();
                } catch (e) {
                  console.error(e);
                }
              } else {
                if (!identity?.company) {
                  handleClose();
                  return;
                }
                try {
                  const res = await buyerRequestsMutationApi.addProduct(
                    identity.company.id,
                    {
                      sku: brandId,
                    }
                  );

                  if (res.status === 200) {
                    info("요청 사항이 제출되었습니다.");
                  }
                  handleClose();
                } catch (e) {
                  console.error(e);
                }
              }
            }}
          >
            <Text textStyle={"body-sm-bold"} color={"#FFFFFF"}>
              {tTitle("request_brand_title_modal")}
            </Text>
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
